import {DISCUSSION_READING_LEVELS, DISCUSSION_TONE_LEVELS} from './discussionConstants'
import {defaultMeetingMicAppAllowlist} from './meetingApps'

/** `none` skips the post-survey splash; onboarding is marked complete where it would have shown. */
const onboardingSplashTreatmentNames = ['original', 'pdf', 'none'] as const
export type OnboardingSplashTreatment = (typeof onboardingSplashTreatmentNames)[number]
const desktopOnboardingSplashTreatmentNames = ['inherit', ...onboardingSplashTreatmentNames] as const
export type DesktopOnboardingSplashTreatment = (typeof desktopOnboardingSplashTreatmentNames)[number]

const surveyPageTreatmentNames = ['multiPage', 'singlePage', 'personaSurvey'] as const
export type SurveyPageTreatment = (typeof surveyPageTreatmentNames)[number]
const desktopSurveyPageTreatmentNames = ['inherit', ...surveyPageTreatmentNames] as const
export type DesktopSurveyPageTreatment = (typeof desktopSurveyPageTreatmentNames)[number]

/** Which copy set the Home calendar cards and the events modal read; one entry per copy variant under test. */
const calendarHomeCopyVariants = ['autopilot'] as const
export type CalendarHomeCopyVariant = (typeof calendarHomeCopyVariants)[number]

const subscriptionPaymentIntervals = ['month', 'year', 'month_or_year'] as const
export type SubscriptionPaymentInterval = (typeof subscriptionPaymentIntervals)[number]

const subscriptionDefaultIntervals = ['month', 'year'] as const
export type SubscriptionDefaultInterval = (typeof subscriptionDefaultIntervals)[number]

export const learnDefaultQuizModes = ['practice', 'test', 'memorize'] as const
export type LearnDefaultQuizMode = (typeof learnDefaultQuizModes)[number]

export const anthropicModels = [
  'claude-haiku-4-5-20251001',
  'claude-sonnet-4-5-20250929',
  'claude-opus-4-1-20250805'
] as const
export type ExperimentAnthropicModel = (typeof anthropicModels)[number]

export const discoverCtaContent = ['signIn', 'tryWebApp'] as const

export type DiscoverCtaContent = (typeof discoverCtaContent)[number]

/**
 * Use this prefix to allow a setting to be applied for test in the web app.
 */
export const webAppExperimentValuePrefix = 'app_'

export interface ExperimentValueTable {
  discussionTone: (typeof DISCUSSION_TONE_LEVELS)[number]
  discussionReadingLevel: (typeof DISCUSSION_READING_LEVELS)[number]
  discussionPlusModel: ExperimentAnthropicModel
  discussionProModel: ExperimentAnthropicModel
  gallerySimilarNoteCount: number
  galleryAllowTrendingSort: boolean
  galleryTrendingTimeframe: number
  galleryDiscoverTagCount: number
  galleryDiscoverNotesPerTag: number
  galleryDiscoverPreloadedRows: number
  galleryDiscoverCtaContent: DiscoverCtaContent
  marketingGiftPageEnabled: boolean
  referrals: boolean
  marketingGiftBannerEnabled: boolean
  marketingLearnGuaranteeRefundPageEnabled: boolean
  marketingStudentsPageEnabled: boolean
  marketingConstructionPageEnabled: boolean
  marketingSportsPageEnabled: boolean
  marketingLawPageEnabled: boolean
  marketingWealthManagementPageEnabled: boolean
  marketingFaqPageEnabled: boolean
  marketingBluebeamPageEnabled: boolean
  marketingIntegrationsPageEnabled: boolean
  marketingDiscountBannerEnabled: boolean
  webAppMarketingEnabled: boolean
  // Gates the desktop-download launch surfaces across marketing, the web app, and login: the
  // `/apps` page, the nav "web" → "desktop" swap, and the Mac/Windows upsell banners.
  desktopDownloadEnabled: boolean
  // Gates the `/meetings` public-launch landing page only. Separate from `desktopDownloadEnabled` so
  // the campaign page can go live on its own schedule, independent of the download surfaces.
  marketingMeetingsPageEnabled: boolean
  // Gates the `/about-us` page and its footer link together, so the link never leads to a 404.
  marketingAboutUsPageEnabled: boolean
  // Gates the `/in-the-media` press page and its footer link together, so the link never leads to a 404.
  marketingInTheMediaPageEnabled: boolean
  marketingInplaceInterestPageEnabled: boolean
  // Turn on before `turnstileLoginMode` reaches `on` for web users: a sign-in the server holds to
  // the Turnstile challenge needs the token this widget produces.
  turnstileWebLoginEnabled: boolean
  turnstileWebEnrollEnabled: boolean
  passwordSignupsEnabled: boolean
  loginDisableRedirect: boolean
  signInSignUpSplitScreen: boolean
  sharedNoteSignInPreview: boolean
  /**
   * A Google sign-in started by the desktop shell also asks for read-only calendar events, so the
   * API can store a calendar connection at sign-in and the desktop gate's connect step is skipped.
   */
  desktopGoogleSignInRequestsCalendar: boolean
  youtubeTranscripts: boolean
  subscriptionProAvailable: boolean
  subscriptionPaymentInterval: SubscriptionPaymentInterval
  subscriptionDefaultInterval: SubscriptionDefaultInterval
  altSub: string
  analyticsRestrictReportingToNewExperiments: boolean
  app_allowGoogleDrivesImports: boolean
  app_allowOfficeFileImports: boolean
  app_allowRecordingOnSafari: boolean
  app_onboardingSplashTreatment: OnboardingSplashTreatment
  /** Desktop's own splash bucket; 'inherit' follows app_onboardingSplashTreatment. */
  app_desktopOnboardingSplashTreatment: DesktopOnboardingSplashTreatment
  /** Desktop shows the starter onboarding home (the onboarding tour). Off: the shell's own onboarding is the persona step and the calendar gate. */
  app_desktopOnboardingHomeEnabled: boolean
  app_surveyPageTreatment: SurveyPageTreatment
  /** Desktop's own survey bucket; 'inherit' follows app_surveyPageTreatment so a browser experiment carries over until desktop is flipped on its own. */
  app_desktopSurveyPageTreatment: DesktopSurveyPageTreatment
  app_enableOnboardingTour: boolean
  app_achievementsEnabled: boolean
  app_discussionEnabled: boolean
  app_incrementalLexicalUpdates: boolean
  app_valentinesDayBrandMark: boolean
  app_backToSchoolBrandMark: boolean
  /** Slideshow announcing new features; ships on, so this is a kill switch. */
  app_featureAnnouncementEnabled: boolean
  app_pasteInNewNoteEnabled: boolean
  app_sharedQuizzesEnabled: boolean
  app_studentBetterGradesOnboarding: boolean
  // The mode an auto-started session opens in. Web counterpart of iOS `learn_default_quiz_mode`.
  app_learnDefaultQuizMode: LearnDefaultQuizMode
  // Offers a note's first Practice question in a popover on the Learn button, at most once a
  // calendar week. Web counterpart of iOS `quizQuestionTip`.
  app_learnQuizQuestionTip: boolean
  // The fewest words a note needs before the quiz question tip is offered on it. Web counterpart of
  // iOS `minimum_words_question_tooltip`.
  app_learnQuizQuestionTipMinimumWords: number
  app_comments: boolean // Cleanup ticket: #53049
  app_businessTrials: boolean
  app_terseSmartNotes: boolean
  // Gates the Text Only view mode on web: a local (unsynced) document-style rendering of
  // qualifying text-only notes at 100% zoom, reflowed to the viewport width.
  app_textOnlyViewMode: boolean
  // Lets web open whiteboard notes (LayoutMode.Whiteboard) instead of refusing them as iPad-only.
  app_whiteboards: boolean
  app_pdfExport: boolean
  // Gates baking imported PDFs' typed form values and annotations into web PDF export.
  // Off copies each source PDF page as is.
  app_pdfExportFillSourcePdfForms: boolean
  app_finishNotes: boolean
  app_finishNotesLiveTranscript: boolean
  app_text_box_shapes: boolean
  app_stickers: boolean
  app_b2bFeatureControls: boolean
  // Nested under app_b2bFeatureControls: enforced SSO shows only when both are on, so it stays
  // independently controllable after app_b2bFeatureControls defaults permanently on.
  app_b2bSsoEnforcement: boolean
  /** Calendar connect, gate and Home cards; ships on, so this is a kill switch. */
  app_calendarSetupEnabled: boolean
  /** Offers Outlook (Microsoft Graph) as a calendar provider alongside Google. Requires app_calendarSetupEnabled. */
  app_calendarProviderMicrosoft: boolean
  /** Syllabus PDF import from the Manage events modal; off until the server's `syllabusAsyncParseEnabled` flag is on. */
  app_learnSyllabusImport: boolean
  app_calendarHomeCopyVariant: CalendarHomeCopyVariant
  /** Desktop gate holds past a connect until the calendar has events or the user adds one by hand. */
  app_calendarGateRequiresEvents: boolean
  /**
   * A/B on the desktop calendar gate; the test arm is a hard gate. Its Later is quiet and shows only while
   * loading or after a failed connect, so everyone else connects, adds an event, or uploads a syllabus to pass.
   * It also offers students Upload syllabus (when syllabus import is on), and its "I don't have a calendar"
   * opens add-your-own-events instead of skipping. Non-student Google / Microsoft sign-ins don't get that link
   * until a connect fails; a failed connect isn't persisted, so each launch asks for one more attempt.
   */
  app_desktopSsoTryConnectBeforeSkip: boolean
  /** First-run cue card on desktop Home's Coming up card; ships on, so this is a kill switch. */
  app_calendarRecordCueCardEnabled: boolean
  /**
   * Reads events from every selected calendar, not just the primary, and shows the re-consent prompt
   * to grants that predate the calendar-list scope. With app_calendarReadonlyScopeEnabled off, also
   * adds the calendar-list scope to Google's connect consent. Only meaningful with
   * app_calendarSetupEnabled on.
   */
  app_calendarSecondaryCalendarsEnabled: boolean
  /**
   * The desktop capture card fires on the worker's per-user relevance prediction instead of on
   * attendee count alone. Off, the worker still scores and learns and the per-series bell is still
   * obeyed; only the prediction is held back. Rolled out as an experiment.
   */
  app_meetingRelevanceGateEnabled: boolean
  /**
   * A desktop scheduled capture card dismissed before its meeting starts comes back once at the
   * start when nothing is recording. On for everyone; kept as an off switch.
   */
  app_meetingCardSecondChanceEnabled: boolean
  /**
   * Google's connect consent and the desktop sign-in ask for the single `calendar.readonly` scope in
   * place of the events and calendar-list pair. Off until Google verifies `calendar.readonly`.
   */
  app_calendarReadonlyScopeEnabled: boolean
  /**
   * Google calendar events' notes and recurring series' folders are read from and saved to the
   * server, so every signed-in device opens the same ones. Off, they stay on this device.
   */
  app_calendarServerLinksEnabled: boolean
  /** Pageless body text starts at the stored top margin, not the page edge. iOS and Android gate the same rule. */
  app_pagelessTopMarginEnabled: boolean
  app_brainBreak: boolean
  /** Lets personal non-Pro accounts open Chat Across Your Notes; the server's 403 paywalls the send instead. */
  app_chatbotPaywallOnSend: boolean
  app_textSearchIndexUploadEnabled: boolean
  app_pdfSearchIndexUploadEnabled: boolean
  // Number of notes a non-paid-tier user can create before hitting the free-tier limit.
  app_freeTierNoteLimit: number
  // While Learn is generating, keep the side panel closed: animate the Learn icon
  // (generating/finished states) and preview the tray in a hover popover instead.
  app_learnGeneratingPopover: boolean
  // Speaker-labeled turns, per-recording sections and turn playback in the transcripts panel.
  app_speakerLabels: boolean
  // Gates the web app's desktop upsell banner alone. `desktopDownloadEnabled` gates the whole download
  // funnel (`/apps`, header CTA, footer, login banner), so it can't turn the banner off by itself.
  app_desktopUpsellBannerEnabled: boolean
  // Shows the Data Detection toggle in Settings > Account. Off only hides the row — an account
  // that already opted out stays opted out — so this is not a kill switch for auto-linking.
  // Named to match the platform counterparts: iOS `dataDetectorSettings`, Android `NOTE_DATA_DETECTION`.
  app_dataDetection: boolean
  // Gates web note links: the note picker in the link popover, in-app note-link following, and
  // rewriting pasted or typed note addresses into the stored form. Off keeps every link a web link.
  app_noteLinks: boolean
  // Serves the MCP consent page (/mcp/consent). The API's `mcpConnector` remote config decides who can
  // connect; this only hides the page until launch.
  app_mcpConsentPageEnabled: boolean
  // Shows Claude and ChatGPT under Settings > Connected services, each opening that app's connector
  // settings. Off until the MCP connector launches, like app_mcpConsentPageEnabled.
  app_mcpConnectedServices: boolean
  // Comma-separated app identifiers the meeting prompt reacts to: macOS
  // bundle-ID prefixes and Windows executable basenames, matched
  // case-insensitively. A server-side edit here is the rapid response to a
  // missing meeting app. The default is generated from MEETING_APP_FAMILIES
  // (meetingApps.ts), which is where a known app's entries belong; a raw
  // entry added here reaches clients but carries no family association.
  // Entry semantics and how to discover an app's identifier:
  // docs/desktop-shell.md, "Microphone-usage detection".
  app_meetingMicAppAllowlist: string
}

export const defaultExperimentValues: ExperimentValueTable = {
  discussionPlusModel: 'claude-haiku-4-5-20251001',
  discussionProModel: 'claude-haiku-4-5-20251001',
  discussionTone: 'formal',
  discussionReadingLevel: 'college',
  gallerySimilarNoteCount: 9,
  galleryAllowTrendingSort: false,
  galleryTrendingTimeframe: 30,
  galleryDiscoverTagCount: 12,
  galleryDiscoverNotesPerTag: 8,
  galleryDiscoverPreloadedRows: 3,
  galleryDiscoverCtaContent: 'tryWebApp',
  marketingGiftPageEnabled: false,
  referrals: false,
  marketingGiftBannerEnabled: false,
  marketingLearnGuaranteeRefundPageEnabled: false,
  marketingStudentsPageEnabled: false,
  marketingConstructionPageEnabled: false,
  marketingSportsPageEnabled: false,
  marketingLawPageEnabled: false,
  marketingWealthManagementPageEnabled: false,
  marketingFaqPageEnabled: false,
  marketingBluebeamPageEnabled: false,
  marketingIntegrationsPageEnabled: false,
  marketingDiscountBannerEnabled: false,
  webAppMarketingEnabled: false,
  desktopDownloadEnabled: false,
  marketingMeetingsPageEnabled: false,
  marketingAboutUsPageEnabled: false,
  marketingInTheMediaPageEnabled: false,
  marketingInplaceInterestPageEnabled: false,
  turnstileWebLoginEnabled: false,
  turnstileWebEnrollEnabled: false,
  passwordSignupsEnabled: true,
  loginDisableRedirect: false,
  signInSignUpSplitScreen: false,
  sharedNoteSignInPreview: false,
  desktopGoogleSignInRequestsCalendar: false,
  subscriptionProAvailable: false,
  subscriptionPaymentInterval: 'year',
  subscriptionDefaultInterval: 'year',
  altSub: '',
  analyticsRestrictReportingToNewExperiments: false,
  youtubeTranscripts: true,
  app_onboardingSplashTreatment: 'original',
  app_desktopOnboardingSplashTreatment: 'none',
  app_desktopOnboardingHomeEnabled: false,
  app_allowGoogleDrivesImports: true,
  app_allowOfficeFileImports: true,
  app_allowRecordingOnSafari: false,
  app_surveyPageTreatment: 'multiPage',
  app_desktopSurveyPageTreatment: 'personaSurvey',
  app_enableOnboardingTour: false,
  app_achievementsEnabled: false,
  app_discussionEnabled: false,
  app_incrementalLexicalUpdates: false,
  app_valentinesDayBrandMark: false,
  app_backToSchoolBrandMark: false,
  app_featureAnnouncementEnabled: true,
  app_pasteInNewNoteEnabled: false,
  app_sharedQuizzesEnabled: false,
  app_studentBetterGradesOnboarding: false,
  app_learnDefaultQuizMode: 'practice',
  app_learnQuizQuestionTip: false,
  app_learnQuizQuestionTipMinimumWords: 3000,
  app_comments: false,
  app_businessTrials: false,
  app_terseSmartNotes: false,
  app_textOnlyViewMode: false,
  app_whiteboards: false,
  app_pdfExport: false,
  app_pdfExportFillSourcePdfForms: false,
  app_finishNotes: false,
  app_finishNotesLiveTranscript: false,
  app_text_box_shapes: false,
  app_stickers: false,
  app_b2bFeatureControls: false,
  app_b2bSsoEnforcement: false,
  app_calendarSetupEnabled: true,
  app_calendarProviderMicrosoft: false,
  app_learnSyllabusImport: false,
  app_calendarHomeCopyVariant: 'autopilot',
  app_calendarGateRequiresEvents: true,
  app_desktopSsoTryConnectBeforeSkip: false,
  app_calendarRecordCueCardEnabled: true,
  app_calendarSecondaryCalendarsEnabled: false,
  app_meetingRelevanceGateEnabled: false,
  app_meetingCardSecondChanceEnabled: true,
  app_calendarReadonlyScopeEnabled: false,
  app_calendarServerLinksEnabled: false,
  app_pagelessTopMarginEnabled: false,
  app_brainBreak: false,
  app_chatbotPaywallOnSend: false,
  app_textSearchIndexUploadEnabled: false,
  app_pdfSearchIndexUploadEnabled: false,
  app_freeTierNoteLimit: 5,
  app_learnGeneratingPopover: false,
  app_speakerLabels: false,
  app_desktopUpsellBannerEnabled: false,
  app_dataDetection: false,
  app_noteLinks: false,
  app_mcpConsentPageEnabled: false,
  app_mcpConnectedServices: false,
  app_meetingMicAppAllowlist: defaultMeetingMicAppAllowlist
}

export const experimentValueOptions: Partial<Record<ExperimentValueKey, readonly ExperimentValue[]>> = {
  app_learnDefaultQuizMode: learnDefaultQuizModes,
  app_onboardingSplashTreatment: onboardingSplashTreatmentNames,
  app_desktopOnboardingSplashTreatment: desktopOnboardingSplashTreatmentNames,
  app_surveyPageTreatment: surveyPageTreatmentNames,
  app_desktopSurveyPageTreatment: desktopSurveyPageTreatmentNames,
  app_calendarHomeCopyVariant: calendarHomeCopyVariants,
  gallerySimilarNoteCount: [6, 9, 12, 15, 21, 30],
  subscriptionDefaultInterval: subscriptionDefaultIntervals,
  subscriptionPaymentInterval: subscriptionPaymentIntervals,
  discussionTone: DISCUSSION_TONE_LEVELS,
  discussionReadingLevel: DISCUSSION_READING_LEVELS,
  discussionPlusModel: anthropicModels,
  discussionProModel: anthropicModels,
  galleryDiscoverCtaContent: discoverCtaContent
}

export type ExperimentValueKey = keyof typeof defaultExperimentValues
export type ExperimentValue = number | boolean | string
export type ExperimentValueOverrideTable = Partial<ExperimentValueTable>
export interface ExperimentValuePair {
  control: ExperimentValue
  variant: ExperimentValue
}
export interface ExperimentValuesMap {
  structured: Partial<Record<ExperimentValueKey, ExperimentValuePair>>
  freeform: Record<string, ExperimentValuePair>
}

function reduceOverrideTable(sourceTable: Partial<Record<string, ExperimentValuePair>>, inVariantGroup: boolean) {
  return Object.entries(sourceTable).reduce(
    (acc, [id]) => {
      const entry = sourceTable[id]
      const newVal = inVariantGroup ? entry?.variant : entry?.control
      if (typeof newVal !== 'undefined') {
        acc[id] = newVal
      }
      return acc
    },
    {} as Record<string, ExperimentValue>
  )
}

export function experimentToOverrideTable(table: ExperimentValuesMap, inVariantGroup: boolean) {
  return {
    structured: reduceOverrideTable(table.structured, inVariantGroup) as Partial<ExperimentValueTable>,
    freeform: reduceOverrideTable(table.freeform, inVariantGroup)
  }
}

export function stringToExperimentKey(str: string) {
  if (typeof defaultExperimentValues[str as ExperimentValueKey] === 'undefined') {
    return undefined
  }

  return str as ExperimentValueKey
}

export function parseExperimentValue(key: ExperimentValueKey, value: string): ExperimentValue {
  const valueType = typeof defaultExperimentValues[key]
  switch (valueType) {
    case 'number':
      return parseFloat(value)
    case 'boolean':
      return value === 'true'
    default:
      return value
  }
}
